/**
 * PhotoDetailModal component - Full-screen story view with in-story carousel and between-story navigation
 */

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader2, MoreVertical, Trash2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useStory, useDeleteStory } from '../../services/stories';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, AlertDescription } from '../ui/alert';
import { LikeButton } from '../social/LikeButton';
import { CommentList } from '../social/CommentList';
import { CommentForm } from '../social/CommentForm';
import { VideoPlayer } from '../video/VideoPlayer';
import { cn } from '../../lib/utils';

interface PhotoDetailModalProps {
  storyId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserClick?: (userId: string) => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

/**
 * Format time ago (e.g., "2 hours ago")
 */
const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const uploaded = new Date(timestamp);
  const seconds = Math.floor((now.getTime() - uploaded.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return uploaded.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Get user initials for avatar fallback
 */
const getUserInitials = (displayName: string): string => {
  return displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const PhotoDetailModal = ({
  storyId,
  open,
  onOpenChange,
  onUserClick,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: PhotoDetailModalProps) => {
  const { user } = useAuth();
  const { data: story, isLoading, isError, error } = useStory(storyId || '');
  const deleteMutation = useDeleteStory();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const showInStoryNavigation = (story?.photoCount ?? 0) > 1;
  const minSwipeDistance = 50;

  // Reset photo index when story changes
  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [storyId]);

  // Keyboard navigation for between-story navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious && onNavigatePrevious) {
        e.preventDefault();
        onNavigatePrevious();
      } else if (e.key === 'ArrowRight' && hasNext && onNavigateNext) {
        e.preventDefault();
        onNavigateNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasPrevious, hasNext, onNavigatePrevious, onNavigateNext]);

  if (!storyId) return null;

  const isOwner = user?.userId === story?.userId;
  const currentPhoto = story?.photos[currentPhotoIndex];

  const handleUserClick = () => {
    if (onUserClick && story?.user) {
      onUserClick(story.user.userId);
      onOpenChange(false);
    }
  };

  const handleDelete = () => {
    if (!story) return;
    deleteMutation.mutate(story.storyId, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        onOpenChange(false);
      },
    });
  };

  // In-story navigation
  const handlePrevPhoto = () => {
    if (!story) return;
    setSlideDirection('left');
    setCurrentPhotoIndex((prev) => (prev === 0 ? story.photos.length - 1 : prev - 1));
  };

  const handleNextPhoto = () => {
    if (!story) return;
    setSlideDirection('right');
    setCurrentPhotoIndex((prev) => (prev === story.photos.length - 1 ? 0 : prev + 1));
  };

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextPhoto();
    } else if (isRightSwipe) {
      handlePrevPhoto();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Slide animation variants
  const slideVariants = {
    enter: (direction: 'left' | 'right') => ({
      x: direction === 'right' ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: 'left' | 'right') => ({
      x: direction === 'right' ? -1000 : 1000,
      opacity: 0,
    }),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-dvh gap-0 overflow-y-auto p-0 transition-all duration-200 md:max-w-6xl md:overflow-hidden"
        hideClose
      >
        {isLoading && (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="flex h-full items-center justify-center p-8">
            <Alert variant="destructive">
              <AlertDescription>Failed to load story. {error?.message}</AlertDescription>
            </Alert>
          </div>
        )}

        {story && currentPhoto && (
          <div className="flex flex-col md:h-[90vh] md:flex-row">
            {/* Left side - Photo/Video with in-story carousel */}
            <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-black md:h-full">
              <AnimatePresence initial={false} custom={slideDirection} mode="wait">
                {currentPhoto.mediaType === 'video' && currentPhoto.videoUrl ? (
                  <motion.div
                    key={currentPhoto.photoId}
                    className="h-auto max-h-full w-auto max-w-full"
                    custom={slideDirection}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: 'spring', stiffness: 300, damping: 30 },
                      opacity: { duration: 0.2 },
                    }}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  >
                    <VideoPlayer
                      src={currentPhoto.videoUrl}
                      poster={currentPhoto.images.full}
                      className="h-auto max-h-full w-auto max-w-full"
                    />
                  </motion.div>
                ) : (
                  <motion.img
                    key={currentPhoto.photoId}
                    src={currentPhoto.images.full}
                    alt={story.caption || 'Story photo'}
                    className="h-auto max-h-full w-auto max-w-full object-contain"
                    custom={slideDirection}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: 'spring', stiffness: 300, damping: 30 },
                      opacity: { duration: 0.2 },
                    }}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    draggable={false}
                  />
                )}
              </AnimatePresence>

              {/* In-story navigation buttons - only show if more than one photo */}
              {showInStoryNavigation && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 hover:text-white"
                    onClick={handlePrevPhoto}
                    aria-label="Previous photo in story"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 hover:text-white"
                    onClick={handleNextPhoto}
                    aria-label="Next photo in story"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              )}

              {/* Dot indicators - only show if more than one photo */}
              {showInStoryNavigation && (
                <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
                  {story.photos.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        'h-2 w-2 rounded-full transition-all',
                        index === currentPhotoIndex ? 'w-6 bg-white' : 'bg-white/60'
                      )}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right side - Details */}
            <div className="flex w-full flex-col bg-background md:h-full md:w-96">
              {/* Header - User info */}
              <div className="flex items-center justify-between border-b p-4">
                {/* Left side - User info */}
                <button
                  onClick={handleUserClick}
                  className="flex items-center gap-3 text-left transition-opacity hover:opacity-80"
                >
                  <Avatar>
                    <AvatarImage src={story.user?.avatarUrl} alt={story.user?.displayName} />
                    <AvatarFallback>
                      {getUserInitials(story.user?.displayName || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold">{story.user?.displayName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatTimeAgo(story.uploadedAt)}
                    </p>
                  </div>
                </button>

                {/* Right side - Action buttons */}
                <div className="flex items-center gap-1">
                  {isOwner && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setShowDeleteDialog(true)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Story
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* Close button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    className="h-8 w-8"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Caption */}
              {story.caption && (
                <div className="border-b p-4">
                  <button
                    onClick={handleUserClick}
                    className="mr-2 text-sm font-semibold transition-opacity hover:opacity-80"
                  >
                    {story.user?.displayName}
                  </button>
                  <span className="text-sm">{story.caption}</span>
                </div>
              )}

              {/* Comments section */}
              <div className="flex-1 overflow-y-auto p-4">
                <CommentList photoId={story.storyId} currentUserId="current-user" />
              </div>

              {/* Actions bar */}
              <div className="max-w-full space-y-3 overflow-hidden border-t p-4">
                {/* Like button and between-story navigation */}
                <div className="flex items-center justify-between">
                  <LikeButton
                    photoId={story.storyId}
                    isLiked={story.isLikedByCurrentUser || false}
                    likeCount={story.likeCount}
                    size="md"
                  />

                  {/* Between-story navigation buttons */}
                  {(hasPrevious || hasNext) && (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        onClick={onNavigatePrevious}
                        disabled={!hasPrevious}
                        aria-label="Previous story"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        onClick={onNavigateNext}
                        disabled={!hasNext}
                        aria-label="Next story"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <p className="text-xs uppercase text-muted-foreground">
                  {formatTimeAgo(story.uploadedAt)}
                </p>

                {/* Comment form */}
                <div className="pt-2">
                  <CommentForm photoId={story.storyId} />
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Story</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this story? This will delete all photos in the story.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Story'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
